import type { PredictorConfig } from "./config";
import { compareDateTime, dateOf, daysBetween, type IsoDateTime } from "./dates";
import type { Bean, Shot } from "./model";

/**
 * Taste feedback on a bean's target time. Mirrors TasteNudge.java.
 *
 * Considered: the bean's taste-tagged shots from the last 14 days whose normalised time landed inside the current
 * window (target ± targetWindowHalfS, target = recipe target + current nudge). With at least 3 of them:
 *   sour > balanced (and not bitter > balanced) → target +1 s
 *   bitter > balanced (and not sour > balanced) → target −1 s
 * Guards: never when the bean has a hand-set target; the nudge stays within ±4 s of the recipe target; at least 2
 * new tagged shots since the previous nudge; after the first reversal of direction nudging pauses for that bean
 * (undoing a nudge lifts the pause). The log of nudges is the state; undo marks the latest entry undone.
 */
export type NudgeReason = "sour" | "bitter";

export interface NudgeEntry {
  readonly createdAt: IsoDateTime;
  /** +1 or −1 seconds */
  readonly deltaS: number;
  /** the bean's total taste-tagged shots when this nudge was made */
  readonly taggedCount: number;
  readonly reason: NudgeReason;
  readonly undone: boolean;
}

export const NUDGE_LOOKBACK_DAYS = 14;
export const NUDGE_MIN_TAGGED = 3;
export const NUDGE_MIN_NEW_TAGGED = 2;
export const NUDGE_STEP_S = 1.0;
export const NUDGE_MAX_OFFSET_S = 4.0;

export interface NudgeState {
  /** sum of the active (not undone) nudges */
  readonly offsetS: number;
  /** true once the active nudges include both directions */
  readonly paused: boolean;
  /** latest active nudge */
  readonly last: NudgeEntry | null;
}

export function activeNudges(log: readonly NudgeEntry[]): NudgeEntry[] {
  return log.filter((e) => !e.undone).sort((a, b) => compareDateTime(a.createdAt, b.createdAt));
}

export function nudgeState(log: readonly NudgeEntry[]): NudgeState {
  const active = activeNudges(log);
  let offset = 0, up = false, down = false;
  for (const e of active) { offset += e.deltaS; if (e.deltaS > 0) up = true; else down = true; }
  return { offsetS: offset, paused: up && down, last: active[active.length - 1] ?? null };
}

export type NudgeBlock = "hand-set" | "paused" | "spacing" | "too-few" | "no-majority" | "at-limit";

export interface NudgeDecision {
  readonly nudge: { readonly deltaS: number; readonly reason: NudgeReason; readonly taggedCount: number; readonly newOffsetS: number } | null;
  readonly blocked: NudgeBlock | null;
}

/** `shots` are the bean's shots on any grinder; `cfg` has the recipe applied (no per-bean target). */
export function tasteNudge(bean: Bean, shots: readonly Shot[], log: readonly NudgeEntry[], now: IsoDateTime,
                           cfg: PredictorConfig): NudgeDecision {
  const no = (blocked: NudgeBlock): NudgeDecision => ({ nudge: null, blocked });
  if (bean.targetTimeS !== null) return no("hand-set");
  const st = nudgeState(log);
  if (st.paused) return no("paused");

  const tagged = shots.filter((s) => s.taste !== null);
  if (st.last !== null && tagged.length < st.last.taggedCount + NUDGE_MIN_NEW_TAGGED) return no("spacing");

  const target = cfg.targetTimeS + st.offsetS;
  const lo = target - cfg.targetWindowHalfS, hi = target + cfg.targetWindowHalfS;
  const today = dateOf(now);
  let sour = 0, balanced = 0, bitter = 0, n = 0;
  for (const s of tagged) {
    if (daysBetween(dateOf(s.timestamp), today) > NUDGE_LOOKBACK_DAYS) continue;
    const tNorm = (s.timeSeconds * cfg.targetYieldG) / s.yieldG;
    if (tNorm < lo || tNorm > hi) continue;
    n++;
    if (s.taste === "sour") sour++;
    else if (s.taste === "bitter") bitter++;
    else balanced++;
  }
  if (n < NUDGE_MIN_TAGGED) return no("too-few");
  const moreSour = sour > balanced, moreBitter = bitter > balanced;
  if (moreSour === moreBitter) return no("no-majority");
  const deltaS = moreSour ? NUDGE_STEP_S : -NUDGE_STEP_S;
  const newOffsetS = st.offsetS + deltaS;
  if (Math.abs(newOffsetS) > NUDGE_MAX_OFFSET_S + 1e-9) return no("at-limit");
  return { nudge: { deltaS, reason: moreSour ? "sour" : "bitter", taggedCount: tagged.length, newOffsetS }, blocked: null };
}

/** The bean the engine should see: a nudged target applied as its target time (hand-set targets win). */
export function withNudgedTarget(bean: Bean, offsetS: number, cfg: PredictorConfig): Bean {
  if (bean.targetTimeS !== null || offsetS === 0) return bean;
  return { ...bean, targetTimeS: cfg.targetTimeS + offsetS };
}
